import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatINR, formatDate, roleLabel } from "@/lib/constants";
import {
  ArrowLeft, Mail, Phone, User, IndianRupee, Target, ShieldCheck, HeartPulse,
  MessageSquare, Calendar, CheckCircle2, ArrowRight, Wand2, Send, PencilLine, Loader2,
  Sparkles, RotateCcw, ClipboardList, GripVertical,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const STATUS_TONE = {
  New: "bg-blue-50 text-blue-700 border-blue-200",
  Contacted: "bg-amber-50 text-amber-700 border-amber-200",
  "Discovery Call Scheduled": "bg-purple-50 text-purple-700 border-purple-200",
  "Discovery Call Done": "bg-emerald-50 text-emerald-700 border-emerald-200",
  Converted: "bg-primary/10 text-primary border-primary/30",
  Dropped: "bg-gray-100 text-gray-500 border-gray-200",
};

const SUMMARY_FIELDS = [
  { key: "health_insurance", label: "Health insurance", placeholder: "• Existing cover / gaps\n• Family members covered\n• Renewals / claims history" },
  { key: "term_insurance", label: "Term insurance", placeholder: "• Existing cover vs required\n• Dependants / liabilities" },
  { key: "emergency_fund", label: "Emergency fund", placeholder: "• Current buffer months\n• Where it's parked" },
  { key: "existing_portfolio", label: "Existing portfolio", placeholder: "• MFs / EPF / FDs / other\n• Concentration or overlap seen" },
  { key: "help_required", label: "Help required", placeholder: "• Specific pain points\n• What client wants first" },
  { key: "basic_goals", label: "Basic goals", placeholder: "• Retirement, kids' education, home, etc.\n• Rough time horizons" },
  { key: "next_steps", label: "Next steps", placeholder: "• Goal-mapping session date\n• Documents to collect\n• Anything else agreed" },
];

const Field = ({ label, value, icon: Icon }) => (
  <div className="flex items-start gap-3">
    {Icon && <Icon className="mt-1 h-4 w-4 shrink-0 text-primary/70" />}
    <div className="min-w-0">
      <p className="overline text-[10px] text-muted-foreground">{label}</p>
      <p className="text-sm font-medium text-foreground break-words">{value ?? "—"}</p>
    </div>
  </div>
);

export default function ProspectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ notes: {}, ai_generated_draft: "", status: "Draft" });
  const [busy, setBusy] = useState({ save: false, generate: false, send: false, prep: false, snooze: false });

  const generatePrep = async () => {
    setBusy(b => ({ ...b, prep: true }));
    try {
      const { data } = await api.post(`/prospects/${id}/prep-card/generate`);
      setP(prev => ({
        ...prev,
        prep_card_html: data.prep_card,
        prep_card_generated_at: data.generated_at,
        talking_points: data.talking_points || [],
      }));
      toast.success("Prep card ready");
    } catch (err) { toast.error(err.response?.data?.detail || "Prep card failed"); }
    finally { setBusy(b => ({ ...b, prep: false })); }
  };

  const toggleTalkingPoint = async (tp) => {
    const nextDiscussed = !tp.discussed;
    // Optimistic update — snap the UI first, roll back on API error.
    setP(prev => ({
      ...prev,
      talking_points: (prev.talking_points || []).map(x =>
        x.id === tp.id ? { ...x, discussed: nextDiscussed } : x
      ),
    }));
    try {
      const { data } = await api.patch(`/prospects/${id}/talking-points/${tp.id}`, { discussed: nextDiscussed });
      setP(prev => ({ ...prev, talking_points: data.talking_points }));
    } catch (err) {
      toast.error("Couldn't update — please retry");
      setP(prev => ({
        ...prev,
        talking_points: (prev.talking_points || []).map(x =>
          x.id === tp.id ? { ...x, discussed: tp.discussed } : x
        ),
      }));
    }
  };

  const pullTalkingPointsIntoNotes = () => {
    const discussed = (p.talking_points || []).filter(t => t.discussed);
    if (discussed.length === 0) { toast.error("No talking points marked as discussed yet"); return; }
    const asBullets = discussed.map(t => `• ${t.text}`).join("\n");
    setSummary(s => ({
      ...s,
      notes: {
        ...s.notes,
        help_required: (s.notes.help_required || "").trim()
          ? `${s.notes.help_required.trim()}\n${asBullets}`
          : asBullets,
      },
    }));
    toast.success(`${discussed.length} talking point${discussed.length === 1 ? "" : "s"} copied to notes`);
  };

  // ---- Drag-to-reorder talking points ----
  const [dragging, setDragging] = useState(null); // { id, from }
  const [dragOver, setDragOver] = useState(null);

  const onDragStart = (i, tpId) => (e) => {
    setDragging({ id: tpId, from: i });
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires setData
    try { e.dataTransfer.setData("text/plain", tpId); } catch {}
  };
  const onDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOver !== i) setDragOver(i);
  };
  const onDrop = (i) => async (e) => {
    e.preventDefault();
    if (!dragging || dragging.from === i) { setDragging(null); setDragOver(null); return; }
    const list = [...(p.talking_points || [])];
    const [moved] = list.splice(dragging.from, 1);
    list.splice(i, 0, moved);
    setP(prev => ({ ...prev, talking_points: list })); // optimistic
    setDragging(null); setDragOver(null);
    try {
      const { data } = await api.patch(`/prospects/${id}/talking-points/reorder`, {
        order: list.map(t => t.id),
      });
      setP(prev => ({ ...prev, talking_points: data.talking_points }));
    } catch (err) {
      toast.error("Couldn't save new order");
      load(); // resync
    }
  };

  const snoozeDropout = async () => {
    if (!window.confirm("Mark this lead as 'Contacted' and take it off the auto-sweep? You'll follow up personally.")) return;
    setBusy(b => ({ ...b, snooze: true }));
    try {
      await api.post(`/prospects/${id}/snooze-dropout`);
      toast.success("Lead revived — status set to Contacted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(b => ({ ...b, snooze: false })); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/prospects/${id}`);
      setP(data);
      const s = data.latest_summary;
      if (s) setSummary({ notes: s.notes || {}, ai_generated_draft: s.ai_generated_draft || "", status: s.status || "Draft" });
    } catch { toast.error("Prospect not found"); navigate("/app/prospects"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const activeAppt = (p?.appointments || []).find(a => ["Confirmed (Link Pending)", "Link Sent", "Rescheduled"].includes(a.status));

  const setNote = (k, v) => setSummary(s => ({ ...s, notes: { ...s.notes, [k]: v } }));

  const saveNotes = async () => {
    if (!activeAppt) { toast.error("No confirmed appointment on this prospect"); return; }
    setBusy(b => ({ ...b, save: true }));
    try {
      await api.post(`/appointments/${activeAppt.id}/summary`, { notes: summary.notes, ai_generated_draft: summary.ai_generated_draft });
      toast.success("Notes saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setBusy(b => ({ ...b, save: false })); }
  };

  const generate = async () => {
    if (!activeAppt) { toast.error("No confirmed appointment on this prospect"); return; }
    setBusy(b => ({ ...b, generate: true }));
    try {
      await api.post(`/appointments/${activeAppt.id}/summary`, { notes: summary.notes });
      const { data } = await api.post(`/appointments/${activeAppt.id}/summary/generate`);
      setSummary(s => ({ ...s, ai_generated_draft: data.draft, status: "Draft" }));
      toast.success("Draft generated — review and edit before sending");
    } catch (err) { toast.error(err.response?.data?.detail || "Generation failed"); }
    finally { setBusy(b => ({ ...b, generate: false })); }
  };

  const sendEmail = async () => {
    if (!activeAppt) return;
    if (!summary.ai_generated_draft?.trim()) { toast.error("Nothing to send — generate or paste a draft first"); return; }
    if (!window.confirm(`Send this summary to ${p.email}?`)) return;
    setBusy(b => ({ ...b, send: true }));
    try {
      await api.post(`/appointments/${activeAppt.id}/summary/send`, { body: summary.ai_generated_draft });
      toast.success(`Summary emailed to ${p.email}`);
      setSummary(s => ({ ...s, status: "Sent" }));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
    finally { setBusy(b => ({ ...b, send: false })); }
  };

  if (loading) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;
  if (!p) return null;

  const hi = p.current_insurance_health || {};
  const li = p.current_insurance_life || {};

  return (
    <div className="space-y-6" data-testid="prospect-detail">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <button onClick={() => navigate("/app/prospects")} className="mb-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-primary" data-testid="back-to-prospects">
            <ArrowLeft className="h-3 w-3" /> Back to prospects
          </button>
          <p className="overline mb-1 text-muted-foreground">Stage 1 · Discovery</p>
          <h1 className="font-dashboard text-3xl font-bold tracking-tight" data-testid="prospect-name">{p.full_name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className={"rounded-full border px-2.5 py-1 text-xs font-medium " + (STATUS_TONE[p.status] || "bg-gray-100 text-gray-700 border-gray-200")} data-testid="prospect-status">{p.status}</span>
            <span className="text-xs text-muted-foreground">Received {formatDate(p.created_at)}</span>
          </div>
        </div>
        {p.converted_client_id && (
          <Button onClick={() => navigate(`/app/clients/${p.converted_client_id}`)} className="gap-1" data-testid="open-client-btn">
            Open client <ArrowRight className="h-3 w-3" />
          </Button>
        )}
        {p.status === "Dropped" && !p.converted_client_id && (
          <Button variant="outline" onClick={snoozeDropout} disabled={busy.snooze} className="gap-1 border-primary/40 text-primary hover:bg-primary/5" data-testid="snooze-dropout-btn">
            {busy.snooze ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} I'll follow up personally
          </Button>
        )}
      </div>

      {activeAppt && (
        <Card className="border-primary/30 bg-primary/5 p-5" data-testid="prep-card">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 font-dashboard text-sm font-bold text-primary">
              <Sparkles className="h-4 w-4" /> 30-second prep card
            </h3>
            <Button size="sm" variant="ghost" onClick={generatePrep} disabled={busy.prep} className="gap-1 text-primary" data-testid="regen-prep-btn">
              {busy.prep ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} {p.prep_card_html ? "Regenerate" : "Generate"}
            </Button>
          </div>
          {p.prep_card_html ? (
            <div className="prep-card space-y-2 text-sm text-foreground [&_b]:text-primary [&_b]:font-semibold" data-testid="prep-card-body">
              {p.prep_card_html.split("\n\n").filter(Boolean).map((line, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: line.replace(/\n/g, "<br/>") }} />
              ))}
              {p.prep_card_generated_at && (
                <p className="pt-2 text-[10px] text-muted-foreground">Generated {formatDate(p.prep_card_generated_at)}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Get a tight, scannable briefing before the call — cashflow, protection gaps, goals, and 2-3 talking points. Takes ~5 seconds.
            </p>
          )}

          {(p.talking_points || []).length > 0 && (
            <div className="mt-5 rounded-md border border-border bg-white p-4" data-testid="talking-points">
              <div className="mb-3 flex items-center justify-between">
                <p className="flex items-center gap-2 text-xs font-semibold text-foreground">
                  <ClipboardList className="h-3.5 w-3.5 text-primary" />
                  Talking points — drag to prioritise, tick as you discuss
                </p>
                <button
                  onClick={pullTalkingPointsIntoNotes}
                  className="text-[11px] text-primary hover:underline"
                  data-testid="pull-discussed-btn"
                >
                  Copy discussed → notes ↓
                </button>
              </div>
              <ul className="space-y-1">
                {p.talking_points.map((tp, i) => (
                  <li
                    key={tp.id}
                    draggable
                    onDragStart={onDragStart(i, tp.id)}
                    onDragOver={onDragOver(i)}
                    onDrop={onDrop(i)}
                    onDragEnd={() => { setDragging(null); setDragOver(null); }}
                    className={
                      "group flex items-start gap-2 rounded-md px-2 py-2 text-sm transition-colors " +
                      (dragOver === i && dragging && dragging.from !== i ? "bg-primary/10 ring-1 ring-primary/40 " : "") +
                      (dragging && dragging.id === tp.id ? "opacity-50 " : "hover:bg-muted/40 ")
                    }
                    data-testid={`tp-row-${tp.id}`}
                  >
                    <GripVertical className="mt-0.5 h-4 w-4 cursor-grab text-muted-foreground opacity-40 group-hover:opacity-100 active:cursor-grabbing" />
                    <span className="mt-0.5 select-none text-[11px] font-mono text-muted-foreground w-4">{i + 1}</span>
                    <Checkbox
                      checked={!!tp.discussed}
                      onCheckedChange={() => toggleTalkingPoint(tp)}
                      className="mt-0.5"
                      data-testid={`tp-check-${tp.id}`}
                    />
                    <span
                      className={"cursor-pointer " + (tp.discussed ? "text-muted-foreground line-through" : "text-foreground")}
                      onClick={() => toggleTalkingPoint(tp)}
                    >
                      {tp.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="border-border bg-white p-5">
          <h3 className="mb-4 flex items-center gap-2 font-dashboard text-sm font-bold"><User className="h-4 w-4 text-primary" /> KYC Basics</h3>
          <div className="grid grid-cols-2 gap-4">
            <Field icon={Mail} label="Email" value={p.email} />
            <Field icon={Phone} label="Mobile" value={p.mobile} />
            <Field label="Age" value={p.age} />
            <Field label="Marital status" value={p.marital_status} />
            <Field label="Occupation" value={p.occupation} />
            <Field label="Marketing opt-in" value={p.marketing_opt_in ? "Yes" : "No"} />
          </div>
        </Card>

        <Card className="border-border bg-white p-5">
          <h3 className="mb-4 flex items-center gap-2 font-dashboard text-sm font-bold"><IndianRupee className="h-4 w-4 text-primary" /> Financial snapshot</h3>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Monthly income" value={formatINR(p.monthly_income)} />
            <Field label="Monthly expenditure" value={formatINR(p.monthly_expenditure)} />
            <Field label="Surplus to invest" value={formatINR(p.surplus_available_to_invest)} />
          </div>
          <div className="mt-4 border-t border-border pt-4">
            <p className="overline text-[10px] text-muted-foreground">Existing investments</p>
            <p className="mt-1 whitespace-pre-line text-sm">{p.current_investments_summary || "—"}</p>
          </div>
        </Card>

        <Card className="border-border bg-white p-5">
          <h3 className="mb-4 flex items-center gap-2 font-dashboard text-sm font-bold"><Target className="h-4 w-4 text-primary" /> Goals & expectations</h3>
          <div>
            <p className="overline text-[10px] text-muted-foreground">Financial goals</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(p.financial_goals || []).length > 0 ? p.financial_goals.map(g => (
                <Badge key={g} variant="secondary" className="text-[11px]">{g}</Badge>
              )) : <span className="text-sm text-muted-foreground">—</span>}
            </div>
          </div>
          {p.financial_goals_note && (
            <div className="mt-4 border-t border-border pt-4">
              <p className="overline text-[10px] text-muted-foreground">Goals note</p>
              <p className="mt-1 whitespace-pre-line text-sm">{p.financial_goals_note}</p>
            </div>
          )}
          <div className="mt-4 border-t border-border pt-4">
            <p className="overline text-[10px] text-muted-foreground">Expectations from us</p>
            <p className="mt-1 whitespace-pre-line text-sm">{p.expectations_from_us || "—"}</p>
          </div>
        </Card>

        <Card className="border-border bg-white p-5">
          <h3 className="mb-4 flex items-center gap-2 font-dashboard text-sm font-bold"><ShieldCheck className="h-4 w-4 text-primary" /> Existing insurance</h3>
          <div className="space-y-4">
            <div className="rounded-md border border-border p-3">
              <div className="flex items-center gap-2 text-xs font-semibold"><HeartPulse className="h-3 w-3 text-primary" /> Health cover</div>
              {hi.has_cover ? (
                <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground">Sum insured:</span> <b>{formatINR(hi.sum_insured)}</b></div>
                  <div><span className="text-muted-foreground">Insurer:</span> <b>{hi.insurer || "—"}</b></div>
                </div>
              ) : <p className="mt-2 text-sm text-muted-foreground">No cover indicated</p>}
            </div>
            <div className="rounded-md border border-border p-3">
              <div className="flex items-center gap-2 text-xs font-semibold"><ShieldCheck className="h-3 w-3 text-primary" /> Life / term cover</div>
              {li.has_cover ? (
                <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground">Sum insured:</span> <b>{formatINR(li.sum_insured)}</b></div>
                  <div><span className="text-muted-foreground">Insurer:</span> <b>{li.insurer || "—"}</b></div>
                </div>
              ) : <p className="mt-2 text-sm text-muted-foreground">No cover indicated</p>}
            </div>
          </div>
        </Card>
      </div>

      <Card className="border-border bg-white p-5" data-testid="appointment-history">
        <h3 className="mb-4 flex items-center gap-2 font-dashboard text-sm font-bold"><Calendar className="h-4 w-4 text-primary" /> Appointment history</h3>
        {(p.appointments || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No appointments yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {p.appointments.map(a => (
              <li key={a.id} className="flex items-center justify-between py-3 text-sm">
                <div>
                  <div className="font-medium">{formatDate(a.slot_datetime_utc)}</div>
                  <div className="text-xs text-muted-foreground">{a.status} · booked {formatDate(a.created_at)}</div>
                </div>
                {a.call_link && <a href={a.call_link} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Call link →</a>}
              </li>
            ))}
          </ul>
        )}
        {p.call_outcome_notes && (
          <div className="mt-4 rounded-md border border-primary/20 bg-primary/5 p-3">
            <p className="overline text-[10px] text-primary">Call outcome notes</p>
            <p className="mt-1 whitespace-pre-line text-sm">{p.call_outcome_notes}</p>
          </div>
        )}
      </Card>

      {activeAppt && (
        <Card className="border-border bg-white p-5" data-testid="discovery-summary-card">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center gap-2 font-dashboard text-sm font-bold"><MessageSquare className="h-4 w-4 text-primary" /> Discovery Call summary</h3>
            {summary.status && <Badge variant="secondary" className="text-[11px]">{summary.status}</Badge>}
          </div>
          <p className="mb-4 text-xs text-muted-foreground">
            Jot down bullets in each section, then generate a polished client-facing email. The AI is prompted to stay within Reliserve Wealth's regulatory scope — MF categories only, no return figures, insurance-as-protection framing, no RIA claims.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {SUMMARY_FIELDS.map(f => (
              <div key={f.key}>
                <Label className="text-xs">{f.label}</Label>
                <Textarea
                  rows={4}
                  value={summary.notes[f.key] || ""}
                  onChange={e => setNote(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="mt-1 text-sm"
                  data-testid={`note-${f.key}`}
                />
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="outline" onClick={saveNotes} disabled={busy.save} className="gap-1" data-testid="save-notes-btn">
              {busy.save ? <Loader2 className="h-3 w-3 animate-spin" /> : <PencilLine className="h-3 w-3" />} Save notes
            </Button>
            <Button onClick={generate} disabled={busy.generate} className="gap-1" data-testid="generate-draft-btn">
              {busy.generate ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} {busy.generate ? "Drafting…" : "Generate summary email"}
            </Button>
          </div>

          <div className="mt-6 border-t border-border pt-6">
            <Label className="text-xs">AI-drafted email — edit freely before sending</Label>
            <Textarea
              rows={14}
              value={summary.ai_generated_draft}
              onChange={e => setSummary(s => ({ ...s, ai_generated_draft: e.target.value }))}
              placeholder="Click 'Generate summary email' above to draft this from your bullets, or paste your own copy here…"
              className="mt-1 font-mono text-sm"
              data-testid="draft-body"
            />
            <div className="mt-3 flex justify-end">
              <Button onClick={sendEmail} disabled={busy.send || !summary.ai_generated_draft?.trim()} className="gap-1" data-testid="send-summary-btn">
                {busy.send ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Send to {p.email}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
